import { Link, createFileRoute } from "@tanstack/react-router";
import { seo } from "@utils/seo";
import i18n from "@modules/shared/localization/i18n";
import {
  allArticlesQueryOptions,
  collectionQueryOptions,
  collectionsQueryOptions,
  useGetAllArticlesQuery,
  useGetCollectionByHandleQuery,
} from "@modules/shopify/hooks";
import { getCountryVariables } from "@/modules/shared/localization/const/countries";
import { ALL_PRODUCTS_COLLECTION } from "@modules/shop/components/ProductsPage";
import { CollectionCards } from "@modules/shop/components/Homepage/CollectionCards";
import { ProductSection } from "@modules/shop/components/Homepage/ProductSection";
import { LatestArticles } from "@modules/shop/components/Homepage/LatestArticles";
import { NewsletterSection } from "@modules/shop/components/Homepage/NewsletterSection";
import { CraftSection } from "@modules/shop/components/Homepage/CraftSection";
import { RevealWords } from "@modules/shop/components/Homepage/RevealWords";
import { useHomepageMotion } from "@modules/shop/components/Homepage/useHomepageMotion";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/$locale/_layout/(public)/_public/")({
  component: Homepage,
  ssr: true,
  loader: async ({ context: { queryClient }, params }) => {
    const localeVars = getCountryVariables(params.locale);

    // Prefetch all homepage data in parallel
    const [, , collectionsData] = await Promise.all([
      // Featured / new products
      queryClient.ensureQueryData(
        collectionQueryOptions({
          handle: "novi-proizvodi-1",
          numberOfProducts: 8,
          ...localeVars,
        }),
      ),
      // Best sellers
      queryClient.ensureQueryData(
        collectionQueryOptions({
          handle: "najprodavanije",
          numberOfProducts: 8,
          ...localeVars,
        }),
      ),
      // All collections (for hero + collections section)
      queryClient.ensureQueryData(
        collectionsQueryOptions({
          first: 250,
          ...localeVars,
        }),
      ),
      // Latest blog articles
      queryClient.ensureQueryData(
        allArticlesQueryOptions({
          first: 3,
          ...localeVars,
        }),
      ),
    ]);

    return {
      collectionsData,
    };
  },
  staleTime: 60_000, // 60s client-side freshness
  headers: () => ({
    "Cache-Control":
      "public, max-age=300, s-maxage=300, stale-while-revalidate=86400",
  }),
  head: ({ params }) => {
    const t = i18n.getFixedT(params.locale);
    return {
      meta: [
        ...seo({
          title: t("Seo:homepage.title"),
          description: t("Seo:homepage.description"),
          image: HOME_HERO_IMAGE_URL,
        }),
      ],
    };
  },
});

const HOME_HERO_IMAGE_URL =
  "https://cdn.shopify.com/s/files/1/0991/8090/4831/files/viber_image_2026-05-16_10-32-37-925.jpg?v=1778924132";

const EXCLUDED_COLLECTION_HANDLES = new Set([
  "novi-proizvodi-1",
  "najprodavanije",
  ALL_PRODUCTS_COLLECTION,
]);

function Homepage() {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  useHomepageMotion(rootRef);
  const { locale } = Route.useParams();
  const { collectionsData } = Route.useLoaderData();
  const localeVars = getCountryVariables(locale);

  // Fetch all homepage data
  const { data: featuredData } = useGetCollectionByHandleQuery({
    handle: "novi-proizvodi-1",
    numberOfProducts: 8,
    ...localeVars,
  });

  const { data: bestSellersData } = useGetCollectionByHandleQuery({
    handle: "najprodavanije",
    numberOfProducts: 8,
    ...localeVars,
  });

  const { data: articlesData } = useGetAllArticlesQuery({
    first: 3,
    ...localeVars,
  });

  const featuredProducts = featuredData?.collection?.products.nodes ?? [];
  const bestSellerProducts = bestSellersData?.collection?.products.nodes ?? [];
  const articles = articlesData?.articles.nodes ?? [];
  const collections = collectionsData.collections.nodes.filter(
    (collection) => !EXCLUDED_COLLECTION_HANDLES.has(collection.handle),
  );

  return (
    <div ref={rootRef} className="pt-6">
      {/* 1. Hero Slider (temporarily disabled) */}
      {/* <HeroSlider locale={locale} slides={heroSlides} /> */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="relative mx-auto flex min-h-[32rem] max-w-7xl items-end overflow-hidden rounded-3xl max-mobile:min-h-[28rem]">
          <img
            src={HOME_HERO_IMAGE_URL}
            alt=""
            className="absolute inset-0 size-full object-cover object-[center_10%]"
            fetchPriority="high"
            data-hero-image
          />
          <div
            className="absolute inset-0 bg-linear-to-tr from-black/80 via-black/45 to-black/0 max-mobile:from-black/85 max-mobile:via-black/65 max-mobile:to-black/45"
            aria-hidden="true"
          />
          <div className="relative z-10 max-w-5xl p-12 max-mobile:p-6">
            <p className="hero-fade text-small font-medium text-white/80">
              {t("Homepage.welcome.kicker")}
            </p>
            <h1 className="mt-3 text-3xlarge leading-[1.05] font-bold tracking-[-0.02em] text-white max-mobile:text-xlarge">
              <RevealWords text={t("Homepage.welcome.title")} hero />
            </h1>
            <p className="hero-fade mt-5 max-w-2xl text-medium leading-relaxed text-pretty text-white/85 max-mobile:text-base">
              {t("Homepage.welcome.subtitle")}
            </p>
            <div className="hero-fade mt-8 flex flex-wrap gap-3 [--d:500ms]">
              <Link
                to="/$locale/products"
                params={{ locale }}
                className="rounded-full bg-white px-6 py-3 text-small font-semibold text-text-1 transition-[background-color,scale] duration-100 ease-out hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black active:scale-[0.97]"
              >
                {t("Homepage.viewProducts")}
              </Link>
              <Link
                to="/$locale/collections"
                params={{ locale }}
                className="rounded-full bg-white/15 px-6 py-3 text-small font-semibold text-white backdrop-blur-md transition-[background-color,scale] duration-100 ease-out hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black active:scale-[0.97]"
              >
                {t("Collections.title")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Collection Cards */}
      <CollectionCards locale={locale} collections={collections} />

      <CraftSection locale={locale} />

      {/* 3. New / Featured Products */}
      <ProductSection
        title={t("Homepage.newProducts")}
        viewAllLabel={t("Homepage.viewAll")}
        viewAllHandle="novi-proizvodi-1"
        locale={locale}
        products={featuredProducts}
      />

      {/* 4. Best Sellers */}
      <ProductSection
        title={t("Homepage.bestSellers")}
        viewAllLabel={t("Homepage.viewAll")}
        viewAllHandle="najprodavanije"
        locale={locale}
        products={bestSellerProducts}
      />

      {/* 5. Latest Blog Articles */}
      <LatestArticles locale={locale} articles={articles} />

      {/* 6. Newsletter */}
      <NewsletterSection locale={locale} />
    </div>
  );
}
